import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface License {
  id: string;
  key: string;
  name: string;
  type: string;
  status: string;
  activationStatus: string;
  activationCount: number;
  activationLimit: number;
  expiresAt?: number;
  createdAt: number;
}

interface LicensesProps {
  user: any;
}

function Licenses({ user: _user }: LicensesProps) {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [newLicense, setNewLicense] = useState({
    name: '',
    type: 'PERPETUAL',
    activationLimit: 1,
    expiresAt: '',
  });

  useEffect(() => {
    fetchLicenses();
  }, [filter]);

  const fetchLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const url = filter === 'all' ? `${apiUrl}/api/licenses` : `${apiUrl}/api/licenses?status=${filter}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLicenses(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString();
  };

  const handleCreateLicense = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const payload: any = {
        name: newLicense.name,
        type: newLicense.type,
        activationLimit: newLicense.activationLimit,
      };

      if (newLicense.expiresAt) {
        payload.expiresAt = new Date(newLicense.expiresAt).getTime();
      }

      const response = await fetch(`${apiUrl}/api/licenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewLicense({ name: '', type: 'PERPETUAL', activationLimit: 1, expiresAt: '' });
        fetchLicenses();
      }
    } catch (error) {
      console.error('Failed to create license:', error);
    }
  };

  const handleViewLicense = (license: License) => {
    setSelectedLicense(license);
    setIsViewDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      ACTIVE: 'success' as const,
      EXPIRED: 'destructive' as const,
      REVOKED: 'destructive' as const,
    };
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading licenses...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Licenses</h2>
          <p className="text-muted-foreground mt-2">
            Manage and monitor your license keys
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>Create New License</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Licenses</CardTitle>
              <CardDescription>
                A list of all licenses in your account
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="filter" className="text-sm">Filter:</Label>
              <select
                id="filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="all">All</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="REVOKED">Revoked</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {licenses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No licenses found. Create your first license to get started!
              </p>
              <Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>Create License</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>License Key</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Activations</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses.map((license) => (
                  <TableRow key={license.id}>
                    <TableCell className="font-medium">{license.name}</TableCell>
                    <TableCell>
                      <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
                        {license.key}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{license.type}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(license.status)}</TableCell>
                    <TableCell>
                      {license.activationCount} / {license.activationLimit}
                    </TableCell>
                    <TableCell>{formatDate(license.expiresAt)}</TableCell>
                    <TableCell>{formatDate(license.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleViewLicense(license)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create License Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New License</DialogTitle>
            <DialogDescription>
              Generate a new license key for your product
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">License Name</Label>
              <Input
                id="name"
                value={newLicense.name}
                onChange={(e) => setNewLicense({ ...newLicense, name: e.target.value })}
                placeholder="My License"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type">License Type</Label>
              <Select
                value={newLicense.type}
                onValueChange={(value) => setNewLicense({ ...newLicense, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERPETUAL">Perpetual</SelectItem>
                  <SelectItem value="SUBSCRIPTION">Subscription</SelectItem>
                  <SelectItem value="TRIAL">Trial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="activationLimit">Activation Limit</Label>
              <Input
                id="activationLimit"
                type="number"
                min="1"
                value={newLicense.activationLimit}
                onChange={(e) => setNewLicense({ ...newLicense, activationLimit: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="expiresAt">Expiration Date (Optional)</Label>
              <Input
                id="expiresAt"
                type="date"
                value={newLicense.expiresAt}
                onChange={(e) => setNewLicense({ ...newLicense, expiresAt: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateLicense} disabled={!newLicense.name}>
              Create License
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View License Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedLicense?.name}</DialogTitle>
            <DialogDescription>
              License Details
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>License Key</Label>
                <code className="relative rounded bg-muted px-3 py-2 font-mono text-sm break-all">
                  {selectedLicense.key}
                </code>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Type</Label>
                  <Badge variant="outline">{selectedLicense.type}</Badge>
                </div>
                <div className="grid gap-2">
                  <Label>Status</Label>
                  {getStatusBadge(selectedLicense.status)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Activations</Label>
                  <p className="text-sm">{selectedLicense.activationCount} / {selectedLicense.activationLimit}</p>
                </div>
                <div className="grid gap-2">
                  <Label>Activation Status</Label>
                  <p className="text-sm">{selectedLicense.activationStatus}</p>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Expires</Label>
                <p className="text-sm">{formatDate(selectedLicense.expiresAt)}</p>
              </div>
              <div className="grid gap-2">
                <Label>Created</Label>
                <p className="text-sm">{formatDate(selectedLicense.createdAt)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Licenses;
